import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable, DataTableRef } from '../components/DataTable';
import MapVizModal from '../components/MapVizModal';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import DownloadRawJson from '../components/DownloadRawJson';
import DownloadCsv from '../components/DownloadCsv';
import MapDataButton from '../components/MapDataButton';
import GraphButton from '../components/GraphButton';
import GraphOverlay from '../components/GraphOverlay';
import FilterPanel from '../components/FilterPanel';
import { getTimezoneOffset, convertMinutes } from '../utils/date-time-utils'
import SearchBarComponent from '../components/SearchBarComponent';
import { useAppContext } from '../utils/AppContext';
import { useWorkspaceUrlFilters } from '../hooks/useWorkspaceUrlFilters';
import { useGeopolygonThumbnail } from '../hooks/useGeopolygonThumbnail';
import {
  WorkspaceAppliedFilters,
  hasWorkspaceFilters,
  parseWorkspaceFilters,
} from '../utils/workspaceFilterParams';

// for table row click
export interface DatasetProps {
  id: string;
  name: string;
  created_at: string;
  num_captures: number;
  latest_capture: string;
  earliest_capture: string;
  campaign: string;
  tags: string[];
  publications: {
    title: string;
    author?: string;
    journal?: string;
    year?: number;
    doi: string;
  }[];
  short_id: string;
};

export interface FetchFileListProps {
  count: number;
  next: string;
  previous: string;
  results: DatasetProps[];
};

interface WorkspacePageProps {
  updateCampaignFilter: boolean;
}

const WorkspacePage = (props: WorkspacePageProps) => {
  // state to update campaign filter list
  const { updateCampaignFilter } = props;
  const { geoPolygon, setGeoPolygon, username } = useAppContext();
  const queryClient = useQueryClient();
  const { syncAppliedFiltersToUrl, clearFiltersFromUrl } =
    useWorkspaceUrlFilters();
  const hasHydratedFromUrl = useRef(false);
  const pendingUrlHydrationRefetch = useRef(false);

  const initialFiltersFromUrl = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    if (!hasWorkspaceFilters(params)) {
      return {};
    }
    return parseWorkspaceFilters(params, {
      includeAuthOnlyFilters: Boolean(username),
    });
  }, [username]);

  /** Active geopolygon filter (AppContext only — not stale URL fallback). */
  const geoPolygonQueryString = geoPolygon ? JSON.stringify(geoPolygon) : '';

  const handleGeoPolygonChange = (value: React.SetStateAction<string>) => {
    const newPolygonString =
      typeof value === 'function'
        ? value(JSON.stringify(geoPolygon) || '')
        : value;
    try {
      if (newPolygonString) {
        setGeoPolygon(JSON.parse(newPolygonString));
      } else {
        setGeoPolygon(undefined);
      }
    } catch (error) {
      console.error('Failed to parse polygon string:', error);
      setGeoPolygon(undefined);
    }
  };

  // ref for DataTable to access resetAllSelectedIds function
  const dataTableRef = useRef<DataTableRef>(null);

  // show map
  const [showMap, setShowMap] = useState(false);

  // show graph overlay
  const [showGraph, setShowGraph] = useState(false);

  // selected rows from table
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // network type
  const [selectedNetworkType, setSelectedNetworkType] = useState<string>('');

  // map options
  const center: [number, number] = [41.759103, -87.1];
  const zoom = 10;
  const width = '100%';
  const height = '1000px';

  // state for actual search query
  const [searchQuery, setSearchQuery] = useState('');

  // get system UTC/timezone offset
  const systemOfffsetMinute = getTimezoneOffset() * -1;
  // set default UTC/timezone offset based on system time
  const defaultTimezoneOffset = convertMinutes(systemOfffsetMinute);

  // Date/Time state (seed from URL so the first table fetch can use filters)
  const [startDate, setStartDate] = useState<Date | null>(
    () => initialFiltersFromUrl.confirmedStartDate ?? null,
  );
  const [confirmedStartDate, setConfirmedStartDate] = useState<Date | null>(
    () => initialFiltersFromUrl.confirmedStartDate ?? null,
  );
  const [endDate, setEndDate] = useState<Date | null>(
    () => initialFiltersFromUrl.confirmedEndDate ?? null,
  );
  const [confirmedEndDate, setConfirmedEndDate] = useState<Date | null>(
    () => initialFiltersFromUrl.confirmedEndDate ?? null,
  );
  const [timezoneOffset, setTimezoneOffset] = useState<string>(
    () => initialFiltersFromUrl.timezoneOffset ?? defaultTimezoneOffset,
  );

  // Geocoordinates state: 'lat, lon'
  const [northWest, setNorthWest] = useState<string>(
    () => initialFiltersFromUrl.northWest ?? '',
  );
  const [southEast, setSouthEast] = useState<string>(
    () => initialFiltersFromUrl.southEast ?? '',
  );

  // polygon search
  const [geoPolygonImage, setGeoPolygonImage] = useState<string>('');

  // Tags state
  const [tagName, setTagName] = React.useState<string[]>(
    () => initialFiltersFromUrl.tagName ?? [],
  );

  // My Campaigns state
  const [campaignName, setCampaignName] = React.useState<string[]>(
    () => initialFiltersFromUrl.campaignName ?? [],
  );

  // Check if any rows are selected
  const hasSelectedRows = selectedRows.length > 0;

  // Bands state
  const [bandName, setBandName] = React.useState<string[]>(
    () => initialFiltersFromUrl.bandName ?? [],
  );

  const buildAppliedFilters = useCallback(
    (): WorkspaceAppliedFilters => ({
      confirmedStartDate,
      confirmedEndDate,
      timezoneOffset,
      northWest,
      southEast,
      geoPolygon: geoPolygon,
      bandName,
      tagName,
      campaignName,
    }),
    [
      confirmedStartDate,
      confirmedEndDate,
      timezoneOffset,
      northWest,
      southEast,
      geoPolygon,
      bandName,
      tagName,
      campaignName,
    ],
  );

  const handleFiltersApplied = useCallback(() => {
    syncAppliedFiltersToUrl(buildAppliedFilters(), {
      includeAuthOnlyFilters: Boolean(username),
    });
  }, [syncAppliedFiltersToUrl, buildAppliedFilters, username]);

  const handleFiltersCleared = useCallback(() => {
    clearFiltersFromUrl();
  }, [clearFiltersFromUrl]);

  // Geopolygon lives in context; sync from URL before paint when bookmarked
  useLayoutEffect(() => {
    if (initialFiltersFromUrl.geoPolygon) {
      setGeoPolygon(initialFiltersFromUrl.geoPolygon);
    }
  }, [initialFiltersFromUrl.geoPolygon, setGeoPolygon]);

  useGeopolygonThumbnail(geoPolygon, geoPolygonImage, setGeoPolygonImage);

  // Mark refetch needed when URL had filters (queryKey omits filter fields)
  useEffect(() => {
    if (hasHydratedFromUrl.current) {
      return;
    }
    hasHydratedFromUrl.current = true;
    if (Object.keys(initialFiltersFromUrl).length > 0) {
      pendingUrlHydrationRefetch.current = true;
    }
  }, [initialFiltersFromUrl]);

  // Refetch after hydrated filter state is committed (see geoPolygonQueryString
  // for URL geopolygon on the first paint before context is updated).
  useEffect(() => {
    if (!pendingUrlHydrationRefetch.current) {
      return;
    }
    pendingUrlHydrationRefetch.current = false;
    queryClient.invalidateQueries({ queryKey: ['sigcap_datatable'] });
  }, [
    confirmedStartDate,
    confirmedEndDate,
    northWest,
    southEast,
    geoPolygonQueryString,
    bandName,
    tagName,
    campaignName,
    queryClient,
  ]);

  // callback to reset selected IDs when filters change
  const handleFilterChange = () => {
    if (dataTableRef.current) {
      dataTableRef.current.resetAllSelectedIds();
    }
  };

  // reset selected IDs when search query changes
  useEffect(() => {
    if (dataTableRef.current) {
      dataTableRef.current.resetAllSelectedIds();
    }
  }, [searchQuery]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'top',
        justifyContent: 'center',
        marginTop: '2em',
      }}
    >
      {/* Graph Overlay */}
      {showGraph && (
        <GraphOverlay
          selectedRows={selectedRows}
          onClose={() => setShowGraph(false)}
        />
      )}
      {/* Map Visualization: modal */}
      {showMap && (
        <MapVizModal
          showMap={showMap}
          setShowMap={setShowMap}
          selectedRows={selectedRows}
          selectedNetworkType={selectedNetworkType}
          center={center}
          zoom={zoom}
          height={height}
          width={width}
        />
      )}
      {/* Filter panel */}
      <div
        style={{
          width: '20%',
        }}
      >
        <div
          style={{
            fontSize: '1.5em',
            fontWeight: 'bold',
            marginBottom: '0.5em',
            marginTop: '2.8em',
          }}
        >
          Filters
        </div>
        <FilterPanel
          updateCampaignFilter={updateCampaignFilter}
          startDate={startDate}
          setStartDate={setStartDate}
          setConfirmedStartDate={setConfirmedStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          setConfirmedEndDate={setConfirmedEndDate}
          timezoneOffset={timezoneOffset}
          setTimezoneOffset={setTimezoneOffset}
          defaultTimezoneOffset={defaultTimezoneOffset}
          northWest={northWest}
          setNorthWest={setNorthWest}
          southEast={southEast}
          setSouthEast={setSouthEast}
          tagName={tagName}
          setTagName={setTagName}
          campaignName={campaignName}
          setCampaignName={setCampaignName}
          geoPolygon={geoPolygonQueryString}
          setGeoPolygon={handleGeoPolygonChange}
          geoPolygonImage={geoPolygonImage}
          setGeoPolygonImage={setGeoPolygonImage}
          bandName={bandName}
          setBandName={setBandName}
          onFilterChange={handleFilterChange}
          onFiltersApplied={handleFiltersApplied}
          onFiltersCleared={handleFiltersCleared}
        />
      </div>
      <div style={{ width: '3%' }}></div>
      <div
        style={{
          width: '72%',
        }}
      >
        {/* Search bar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '1em',
          }}
        >
          <SearchBarComponent setSearchQuery={setSearchQuery} />
        </div>
        {/* open Map modal and Export to JSON & CSV */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            marginBottom: '1em',
          }}
        >
          {/* Graph overlay */}
          <GraphButton
            onShowGraph={() => setShowGraph(true)}
            styleOverrides={{ marginRight: '1em' }}
            disabled={!hasSelectedRows}
          />
          {/* Map modal */}
          <MapDataButton
            selectedRows={selectedRows}
            setShowMap={setShowMap}
            setSelectedNetworkType={setSelectedNetworkType}
            styleOverrides={{ marginRight: '1em' }}
            disabled={!hasSelectedRows}
          />
          {/* Export to JSON & CSV */}
          <DownloadCsv
            selectedRows={selectedRows}
            selectedNetworkType={selectedNetworkType}
            styleOverrides={{ marginRight: '1em' }}
            disabled={!hasSelectedRows}
            bandName={bandName}
          />
          <DownloadRawJson
            selectedRows={selectedRows}
            selectedNetworkType={selectedNetworkType}
            disabled={!hasSelectedRows}
          />
        </div>
        {/* Data Table */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DataTable
            // forwardRef to access resetAllSelectedIds function
            ref={dataTableRef}
            setSelectedRows={setSelectedRows}
            searchQuery={searchQuery}
            confirmedStartDate={confirmedStartDate}
            confirmedEndDate={confirmedEndDate}
            northWest={northWest}
            southEast={southEast}
            geoPolygon={geoPolygonQueryString}
            tagName={tagName}
            campaignName={campaignName}
            bandName={bandName}
          />
        </LocalizationProvider>
      </div>
    </div>
  );
};

export default WorkspacePage;
